/**
 * This is copy paste from the following article on Medium
 * https://medium.com/@sergeygultyayev/use-popper-js-in-angular-projects-7b34f18da1c
 */
import {
  Directive,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Renderer2,
  SimpleChanges,
} from '@angular/core';
import {
  Placement,
  createPopper,
  OptionsGeneric,
  Modifier,
  Instance as Popper,
} from '@popperjs/core';
import { fromEvent, merge, Subject } from 'rxjs';
import { filter, pluck, takeUntil } from 'rxjs/operators';

export type PopperOptions = Partial<
  OptionsGeneric<Partial<Modifier<any, any>>>
>;

@Directive({
  selector: '[i2gPopper]',
})
export class PopperDirective implements OnInit, OnDestroy, OnChanges {
  // The hint to display
  @Input() target: HTMLElement;
  // Its positioning (check docs for available options)
  @Input() placement?: Placement;
  // Determines whether the tooltip is shown on hover or click
  @Input() triggerOnClick = false;
  // Optional hint target if you desire using other element than specified one
  @Input() i2gPopper?: HTMLElement;
  // The popper instance
  private popper: Popper;
  private readonly defaultConfig: PopperOptions = {
    placement: 'top',
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [0, 10],
        },
      },
    ],
  };

  private readonly _destroyed = new Subject<void>();
  private isTooltipOpen = false;

  constructor(
    private readonly el: ElementRef,
    private readonly _renderer: Renderer2
  ) {}

  ngOnInit(): void {
    // An element to position the hint relative to
    const reference = this.i2gPopper ? this.i2gPopper : this.el.nativeElement;

    this.popper = createPopper(reference, this.target, {
      ...this.defaultConfig,
      placement: this.placement || this.defaultConfig.placement,
    });

    this._renderer.setStyle(this.target, 'display', 'none');

    if (this.triggerOnClick) {
      this._renderer.listen('window', 'click', (e: Event) => {
        /**
         * Only run when the click is not on the context menu
         * button and not inside the tooltip being shown
         */
        if (e.target !== reference && e.target !== this.target) {
          this.hideTooltip();
          this.isTooltipOpen = false;
        }
      });

      // monitor clicks :)
      fromEvent(reference, 'click')
        .pipe(
          filter(() => this.popper !== null),
          pluck('type'),
          takeUntil(this._destroyed)
        )
        .subscribe(() => this.mouseClickHandler());
    } else {
      // This captures mouse enter and mouse leave events and show tooltip
      // content base on that
      merge(
        fromEvent(reference, 'mouseenter'),
        fromEvent(reference, 'mouseleave')
      )
        .pipe(
          filter(() => this.popper !== null),
          pluck('type'),
          takeUntil(this._destroyed)
        )
        .subscribe((eventType: string) => this.mouseHoverHandler(eventType));
    }
  }

  ngOnChanges(simpleChanges: SimpleChanges) {
    if (
      simpleChanges.placement &&
      simpleChanges.placement.currentValue &&
      !!this.popper
    ) {
      this.popper.setOptions({
        placement: simpleChanges.placement.currentValue,
      });
    }
  }

  ngOnDestroy(): void {
    if (!this.popper) {
      return;
    }

    this.popper.destroy();

    this._destroyed.next();
    this._destroyed.complete();
  }

  private mouseHoverHandler(e: string): void {
    if (e === 'mouseenter') {
      this.showTooltip();
    } else {
      this.hideTooltip();
    }
  }

  private mouseClickHandler(): void {
    if (this.isTooltipOpen) {
      this.hideTooltip();
      this.isTooltipOpen = false;
    } else {
      this.showTooltip();
      this.isTooltipOpen = true;
    }
  }

  private showTooltip() {
    this._renderer.removeStyle(this.target, 'display');
    this.popper.update();
  }

  private hideTooltip() {
    this._renderer.setStyle(this.target, 'display', 'none');
    this.popper.update();
  }
}
